import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle, Archive, ArrowRight, ChevronDown, ChevronRight, Lock, Search, X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { clientRoutes } from "@/lib/clientRoutes";
import { ABC_BADGE, num, RISK_DOT, RISK_LABEL, uah } from "@/lib/clientFormat";
import { dmy } from "@/lib/client360Labels";
import { BUSINESS_MODEL_LABEL, RECORD_TYPE_PILL } from "@/lib/client360Labels";
import { useClientList } from "@/hooks/useClients";
import type { ArchivedFilter, ClientRow, ClientScope, ClientView } from "@/api/clients";

/**
 * Этап 7 — Client Registry («Контрагенти»).
 *
 * Operational registry over the A1 one-list foundation: backend-owned
 * quick-segment counts (§3), rich filters (§4), preset column views (§6),
 * type-aware rich rows (§7) with «останнє замовлення» ≠ «останній контакт» and
 * a 3A next-action, server-side pagination + URL-state with back-from-Client360
 * restore (§8), search privacy + BI-partial (§9/§10). Frontend renders only.
 */

const SCOPE_LABEL: Record<ClientScope, string> = { my: "Мої", team: "Команда", all: "Усі" };
const ARCHIVED_LABEL: Record<ArchivedFilter, string> = { exclude: "Активні", include: "Усі", only: "Тільки архівні" };

const RECORD_TYPE_OPTS: Array<{ v: string; label: string }> = [
  { v: "real_b2b_client", label: "Реальний B2B" },
  { v: "breeder_person", label: "Заводчик" },
  { v: "unknown", label: "Не класифіковано" },
  { v: "service", label: "Службові" },
  // Derive v1.2 [S2] — B2C кінцеві споживачі: hidden-default, доступні цим scope.
  { v: "retail_b2c_customer", label: "Роздрібні B2C" },
];

const BUSINESS_MODEL_OPTS = [
  "independent_pet_shop", "retail_chain", "dealer_partner", "breeder_professional",
  "online_store", "vet_clinic", "grooming_salon", "marketplace_seller", "other",
];

const DEBT_OPTS: Array<{ v: string; label: string }> = [
  { v: "has_debt", label: "Є борг" },
  { v: "has_overdue", label: "Є прострочка" },
  { v: "no_debt", label: "Без боргу" },
  { v: "overdue_gt_7", label: "Прострочка > 7 дн" },
  { v: "overdue_gt_30", label: "Прострочка > 30 дн" },
];

const ORDER_OPTS: Array<{ v: string; label: string }> = [
  { v: "this_month", label: "Є цього місяця" },
  { v: "none_this_month", label: "Немає цього місяця" },
  { v: "no_30", label: "Не купував 30 дн" },
  { v: "no_60", label: "Не купував 60 дн" },
  { v: "no_90", label: "Не купував 90 дн" },
];

const DQ_OPTS: Array<{ v: string; label: string }> = [
  { v: "no_primary_contact", label: "Без осн. контакту" },
  { v: "no_profile", label: "Без профілю" },
  { v: "profile_unverified", label: "Профіль не перевірено" },
  { v: "unclassified", label: "Не класифіковано" },
  { v: "no_next_action", label: "Немає next-action" },
];

// Preset → visible metric columns (§6 / AC-10). Колонки, не пересортування.
type Col = "abc" | "risk" | "rev365" | "rev30" | "debt" | "overdue" | "lastOrder" | "lastContact" | "nextAction";
const PRESETS: Array<{ k: string; label: string; cols: Col[] }> = [
  { k: "standard", label: "Стандартний", cols: ["abc", "risk", "rev365", "debt", "lastOrder", "lastContact", "nextAction"] },
  { k: "finance", label: "Фінанси / борг", cols: ["debt", "overdue", "lastOrder"] },
  { k: "sales", label: "Продажі", cols: ["rev365", "rev30", "lastOrder"] },
  { k: "dq", label: "Data Quality", cols: ["abc", "nextAction"] },
  { k: "manager", label: "Робота менеджера", cols: ["lastContact", "nextAction"] },
];

const DQ_FLAG_LABEL: Record<string, string> = {
  overdue: "прострочка", no_contact: "немає контакту", no_profile: "немає профілю",
  unclassified: "не класифіковано", profile_unverified: "профіль не перевірено",
  no_next_action: "немає next-action", high_risk: "високий ризик",
};

const PAGE_SIZES = [25, 50, 100];

export function ClientsListPage() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  // ── URL-state (back-from-Client360 restore, §8/AC-8) ───────────────────────
  const scope = (sp.get("scope") as ClientScope) || "my";
  const view = (sp.get("view") as ClientView) || "all";
  const segment = sp.get("segment") || "";
  const q = sp.get("q") || "";
  const manager = sp.get("manager") || "";
  const type = sp.get("type") || "";
  const abc = sp.get("abc") || "";
  const risk = sp.get("risk") || "";
  const recordType = sp.get("recordType") || "";
  const businessModel = sp.get("businessModel") || "";
  const debtStatus = sp.get("debtStatus") || "";
  const orderStatus = sp.get("orderStatus") || "";
  const dataQuality = sp.get("dataQuality") || "";
  const city = sp.get("city") || "";
  const archived = (sp.get("archived") as ArchivedFilter) || "exclude"; // D-140 W6
  const preset = sp.get("preset") || "standard";
  const pageSize = Number(sp.get("pageSize")) || 25;
  const page = Math.max(1, Number(sp.get("page")) || 1);
  const cur = sp.get("cur") || "";

  const filterSig = JSON.stringify({ scope, view, segment, q, manager, type, abc, risk, recordType, businessModel, debtStatus, orderStatus, dataQuality, city, archived, pageSize });

  // cursor stack (per filter signature) — sessionStorage so back-nav restores page.
  const stackRef = useRef<{ sig: string; stack: string[] }>({ sig: "", stack: [""] });
  if (stackRef.current.sig !== filterSig) {
    const saved = sessionStorage.getItem("clientRegistry:" + filterSig);
    stackRef.current = { sig: filterSig, stack: saved ? JSON.parse(saved) : [""] };
  }
  const stack = stackRef.current.stack;

  const query = useClientList({
    scope, view, segment, q, manager, type, abc, risk, recordType, businessModel,
    debtStatus, orderStatus, dataQuality, city, archived, cursor: cur, limit: pageSize,
  });
  const data = query.data;

  const biDown = data ? !data.listBiAvailability.abcRiskFiltersAvailable : false;
  const allowedScopes = data?.allowedScopes ?? ["my"];
  const managers = data?.managers ?? [];
  const seg = data?.quickSegments ?? {};
  const rows = data?.items ?? [];
  const cols = PRESETS.find((p) => p.k === preset)?.cols ?? PRESETS[0].cols;
  const has = (c: Col) => cols.includes(c);

  // ── URL mutation helpers ────────────────────────────────────────────────────
  function patch(next: Record<string, string>, resetPage = true) {
    const merged = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(next)) {
      if (v === "") merged.delete(k); else merged.set(k, v);
    }
    if (resetPage) { merged.set("page", "1"); merged.delete("cur"); }
    setSp(merged, { replace: false });
  }

  function setSegment(s: string) {
    // Filter-IA ①/②: scope живе ТІЛЬКИ в сегмент-контролі «Видимість» —
    // швидкі подання = лише operational server-segments.
    patch({ segment: segment === s ? "" : s });
  }

  function goNext() {
    if (!data?.nextCursor) return;
    const ns = [...stack];
    ns[page] = data.nextCursor;
    stackRef.current.stack = ns;
    sessionStorage.setItem("clientRegistry:" + filterSig, JSON.stringify(ns));
    const merged = new URLSearchParams(sp);
    merged.set("page", String(page + 1));
    merged.set("cur", data.nextCursor);
    setSp(merged);
  }
  function goPrev() {
    if (page <= 1) return;
    const merged = new URLSearchParams(sp);
    merged.set("page", String(page - 1));
    const prevCur = stack[page - 2] ?? "";
    if (prevCur) merged.set("cur", prevCur); else merged.delete("cur");
    setSp(merged);
  }

  const expanded = sp.get("exp")?.split(",").filter(Boolean) ?? [];
  function toggleExpand(id: string) {
    const set = new Set(expanded);
    set.has(id) ? set.delete(id) : set.add(id);
    const merged = new URLSearchParams(sp);
    const v = [...set].join(",");
    if (v) merged.set("exp", v); else merged.delete("exp");
    setSp(merged, { replace: true });
  }

  function resetAll() {
    setSp(new URLSearchParams(scope !== "my" ? { scope } : {}));
  }

  const canFilterManager = allowedScopes.includes("all");
  const activeFilterCount = [manager, type, abc, risk, recordType, businessModel, debtStatus, orderStatus, dataQuality, city, q, segment].filter(Boolean).length;

  // ③ — «Більше фільтрів» drawer (advanced: Замовлення / Data Quality / Класифікація).
  const [moreOpen, setMoreOpen] = useState(false);
  const advancedCount = [orderStatus, dataQuality, recordType, type, city].filter(Boolean).length;

  // ③/④ — active-filter chips (рендеряться лише коли застосовані; × знімає фільтр).
  const SEGMENT_LABEL: Record<string, string> = {
    overdueDebt: "З прострочкою", noOrdersThisMonth: "Без замовлень міс.", attention: "Потребують уваги",
    unclassified: "Не класифіковані", breeders: "Заводчики", holdings: "Холдинги",
    technical: "Технічні / службові", profileOnReview: "Профіль на перевірці", highRisk: "Високий ризик",
    noNextAction: "Без next-action", noPrimaryContact: "Без осн. контакту", missingProfile: "Профіль не заповнений",
  };
  const activeChips: Array<{ key: string; label: string; clear: Record<string, string> }> = [];
  if (segment) activeChips.push({ key: "segment", label: SEGMENT_LABEL[segment] ?? segment, clear: { segment: "" } });
  if (archived !== "exclude") activeChips.push({ key: "archived", label: ARCHIVED_LABEL[archived], clear: { archived: "" } });
  if (manager) activeChips.push({ key: "manager", label: `Менеджер: ${managers.find((m) => m.id === manager)?.name ?? manager}`, clear: { manager: "" } });
  if (businessModel) activeChips.push({ key: "businessModel", label: BUSINESS_MODEL_LABEL[businessModel] ?? businessModel, clear: { businessModel: "" } });
  if (abc) activeChips.push({ key: "abc", label: `ABC: ${abc}`, clear: { abc: "" } });
  if (risk) activeChips.push({ key: "risk", label: `Ризик: ${RISK_LABEL[risk as keyof typeof RISK_LABEL] ?? risk}`, clear: { risk: "" } });
  if (debtStatus) activeChips.push({ key: "debtStatus", label: DEBT_OPTS.find((o) => o.v === debtStatus)?.label ?? debtStatus, clear: { debtStatus: "" } });
  if (orderStatus) activeChips.push({ key: "orderStatus", label: `Замовлення: ${ORDER_OPTS.find((o) => o.v === orderStatus)?.label ?? orderStatus}`, clear: { orderStatus: "" } });
  if (dataQuality) activeChips.push({ key: "dataQuality", label: `DQ: ${DQ_OPTS.find((o) => o.v === dataQuality)?.label ?? dataQuality}`, clear: { dataQuality: "" } });
  if (recordType) activeChips.push({ key: "recordType", label: `Класифікація: ${RECORD_TYPE_OPTS.find((o) => o.v === recordType)?.label ?? recordType}`, clear: { recordType: "" } });
  if (type) activeChips.push({ key: "type", label: type === "holding" ? "Холдинги" : "Одиночні", clear: { type: "" } });
  if (city) activeChips.push({ key: "city", label: `Місто: ${city}`, clear: { city: "" } });

  // Search is a controlled input mirrored to the URL on submit.
  const total = data?.totalVisibleCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-[1180px]">
        {/* header + create-request (gated) */}
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Клієнти / Контрагенти</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">холдинги · одиночні · заводчики · службові записи</p>
          </div>
          <button
            type="button"
            disabled
            title="CRM-owned onboarding — майбутня фаза; зараз клієнти приходять з 1С/BI"
            className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground opacity-70"
          >
            <Lock className="h-3.5 w-3.5" /> Запит на створення клієнта
          </button>
        </div>

        <SearchBar
          value={q}
          onSubmit={(v) => patch({ q: v })}
          autoFocusSignal={sp.get("focus") === "search"}
          onFocused={() => {
            // AD-3: ?focus=search — одноразовий (topbar «Пошук клієнтів»/⌘K);
            // знімаємо, щоб не засмічувати URL-state back-restore (§8).
            const merged = new URLSearchParams(sp);
            merged.delete("focus");
            setSp(merged, { replace: true });
          }}
        />

        {/* ①/⑥ scope — ОДИН контрол (backend allowedScopes, resolver — anti-sweep) */}
        <div className="mt-3">
          <div className="mb-1 px-0.5 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">Видимість</div>
          <div className="inline-flex rounded-md border bg-background p-0.5">
            {allowedScopes.map((s) => (
              <button key={s} type="button" onClick={() => patch({ scope: s, segment: "" })}
                className={cn("rounded px-3 py-1 text-xs font-medium transition-colors",
                  scope === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
                {SCOPE_LABEL[s]}
              </button>
            ))}
          </div>
        </div>

        {/* D-140 W6 — archived tri-state (backend-owned: exclude|include|only) */}
        <div className="mt-3">
          <div className="mb-1 px-0.5 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">Архівні клієнти</div>
          <div className="inline-flex rounded-md border bg-background p-0.5">
            {(["exclude", "include", "only"] as ArchivedFilter[]).map((a) => (
              <button key={a} type="button" onClick={() => patch({ archived: a === "exclude" ? "" : a })}
                className={cn("rounded px-3 py-1 text-xs font-medium transition-colors",
                  archived === a ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
                {ARCHIVED_LABEL[a]}
              </button>
            ))}
          </div>
        </div>

        {/* ② швидкі подання — лише operational (без scope-дублів) */}
        <div className="mt-3">
          <div className="mb-1 px-0.5 text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">Швидкі подання</div>
          <QuickSegments seg={seg} segment={segment} onPick={setSegment} />
        </div>

        {/* ③ compact filters: 4-5 видимих + «Більше фільтрів ▾» */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {canFilterManager && (
            <FilterSelect value={manager} onChange={(v) => patch({ manager: v, ...(v ? { scope: "all" } : {}) })}
              placeholder="Менеджер" options={managers.map((m) => ({ v: m.id, label: m.name }))} />
          )}
          <FilterSelect value={businessModel} onChange={(v) => patch({ businessModel: v })}
            placeholder="Бізнес-модель" options={BUSINESS_MODEL_OPTS.map((m) => ({ v: m, label: BUSINESS_MODEL_LABEL[m] ?? m }))} />
          <FilterSelect value={abc} disabled={biDown} onChange={(v) => patch({ abc: v })}
            placeholder="ABC" options={(["A", "B", "C"] as const).map((a) => ({ v: a, label: `ABC: ${a}`, n: data?.facets.abc[a] }))} />
          <FilterSelect value={risk} disabled={biDown} onChange={(v) => patch({ risk: v })}
            placeholder="Ризик" options={(["red", "yellow", "green"] as const).map((r) => ({ v: r, label: RISK_LABEL[r], n: data?.facets.risk[r] }))} />
          <FilterSelect value={debtStatus} disabled={biDown} onChange={(v) => patch({ debtStatus: v })}
            placeholder="Борг" options={DEBT_OPTS} />
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            aria-expanded={moreOpen}
            className={cn(
              "h-9 rounded-md border border-dashed px-3 text-xs font-semibold",
              advancedCount > 0 ? "border-primary/50 text-primary" : "border-input text-muted-foreground hover:text-foreground",
            )}
          >
            Більше фільтрів {advancedCount > 0 ? `(${advancedCount}) ` : ""}{moreOpen ? "▴" : "▾"}
          </button>
        </div>

        {/* ③/④ advanced drawer — Замовлення · Data Quality · Класифікація (один контрол) */}
        {moreOpen && (
          <div className="mt-2 rounded-xl border bg-card px-4 py-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">Замовлення</label>
                <FilterSelect value={orderStatus} onChange={(v) => patch({ orderStatus: v })}
                  placeholder="будь-які" options={ORDER_OPTS} full />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">
                  Data Quality <span className="font-normal">(advanced; «Потребують уваги» — preset вище)</span>
                </label>
                <FilterSelect value={dataQuality} onChange={(v) => patch({ dataQuality: v })}
                  placeholder="будь-яка" options={DQ_OPTS} full />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold text-muted-foreground">Класифікація (тип)</label>
                {/* ④ — «Тип запису» + «Тип» злиті в ОДИН контрол (дві осі, один селект) */}
                <select
                  value={recordType ? `rt:${recordType}` : type ? `st:${type}` : ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "") patch({ recordType: "", type: "" });
                    else if (v.startsWith("rt:")) patch({ recordType: v.slice(3), type: "" });
                    else patch({ type: v.slice(3), recordType: "" });
                  }}
                  className={cn("h-9 w-full rounded-md border border-input bg-background px-2.5 text-xs",
                    (recordType || type) && "border-primary/50 font-medium text-primary")}
                >
                  <option value="">усі типи</option>
                  <optgroup label="Тип запису">
                    {RECORD_TYPE_OPTS.map((o) => {
                      const n = data?.facets.recordTypes?.[o.v];
                      return <option key={o.v} value={`rt:${o.v}`}>{o.label}{n != null ? ` (${n})` : ""}</option>;
                    })}
                  </optgroup>
                  <optgroup label="Структура">
                    <option value="st:holding">Холдинги</option>
                    <option value="st:standalone">Одиночні</option>
                  </optgroup>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ③ active-filter chips — ЛИШЕ коли застосовані */}
        {activeChips.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {activeChips.map((c) => (
              <span key={c.key}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-[11.5px] font-semibold text-primary">
                {c.label}
                <button type="button" aria-label={`Зняти фільтр: ${c.label}`} onClick={() => patch(c.clear)}
                  className="opacity-70 hover:opacity-100">×</button>
              </span>
            ))}
            <button type="button" onClick={resetAll} className="text-xs font-medium text-muted-foreground hover:text-foreground">
              Скинути все ({activeFilterCount})
            </button>
          </div>
        )}

        {biDown && (
          <div className="mt-2 flex items-center gap-2 rounded-lg border border-warning-foreground/20 bg-warning px-3 py-2 text-sm text-warning-foreground">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            BI-метрики (виручка / борг / ризик) тимчасово недоступні. Фільтри ABC/ризику/боргу вимкнено та очищено.
          </div>
        )}

        {/* ⑤ toolbar: count зліва · «Подання» справа (view-control, не фільтр) */}
        <div className="mb-1.5 mt-3 flex flex-wrap items-center justify-between gap-2 px-1">
          <div className="text-sm text-muted-foreground">
            <b className="text-foreground">{total}</b> знайдено
          </div>
          <select value={preset} onChange={(e) => patch({ preset: e.target.value }, false)}
            className="h-8 rounded-md border border-input bg-background px-2.5 text-xs font-semibold text-muted-foreground">
            {PRESETS.map((p) => <option key={p.k} value={p.k}>Подання: {p.label}</option>)}
          </select>
        </div>

        {query.isError ? (
          <ErrorBox onRetry={() => query.refetch()} />
        ) : query.isPending ? (
          <TableSkeleton />
        ) : rows.length === 0 ? (
          <EmptyBox hasFilters={activeFilterCount > 0} onReset={resetAll} />
        ) : (
          <RegistryTable
            rows={rows} cols={cols} has={has} biDown={biDown}
            expanded={expanded} onToggle={toggleExpand}
            onOpen={(id) => navigate(clientRoutes.client(id))}
            onOpenMember={(hid, aid) => navigate(clientRoutes.legalEntity(hid, aid))}
          />
        )}

        {/* pagination */}
        {rows.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span>Показано <b className="text-foreground">{(page - 1) * pageSize + 1}–{(page - 1) * pageSize + rows.length}</b> із <b className="text-foreground">{total}</b></span>
            <div className="flex items-center gap-1">
              <button onClick={goPrev} disabled={page <= 1 || query.isFetching}
                className="rounded-md border bg-background px-2 py-1 disabled:opacity-40">←</button>
              <span className="px-1">Стор. {page} з {totalPages}</span>
              <button onClick={goNext} disabled={!data?.nextCursor || query.isFetching}
                className="rounded-md border bg-background px-2 py-1 disabled:opacity-40">→</button>
            </div>
            <div className="ml-auto flex items-center gap-1">
              <span className="text-xs">Рядків:</span>
              {PAGE_SIZES.map((s) => (
                <button key={s} onClick={() => patch({ pageSize: String(s) })}
                  className={cn("rounded px-2 py-0.5 text-xs", pageSize === s ? "bg-primary/10 font-semibold text-primary" : "hover:text-foreground")}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {/* DS-7 CF-5: risk-traffic-light legend ↔ lib/clientFormat RISK_DOT migrated together
            (green→success-foreground, yellow→warning-foreground, red→destructive); legend ● colors
            match the dot bg tokens 1:1 so traffic-light distinction stays consistent. */}
        <div className="mt-2 text-[11px] text-muted-foreground">
          Ризик: <span className="text-success-foreground">●</span> низький · <span className="text-warning-foreground">●</span> середній · <span className="text-destructive">●</span> високий
        </div>
      </div>
    </div>
  );
}

// ── sub-components ────────────────────────────────────────────────────────────

function SearchBar({
  value,
  onSubmit,
  autoFocusSignal,
  onFocused,
}: {
  value: string;
  onSubmit: (v: string) => void;
  autoFocusSignal?: boolean;
  onFocused?: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocusSignal && ref.current) {
      ref.current.focus();
      onFocused?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocusSignal]);
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSubmit((ref.current?.value ?? "").trim()); }}
      className="relative mb-3"
    >
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={ref} defaultValue={value} key={value}
        placeholder="Пошук клієнта, ЄДРПОУ/ІПН, код 1С, контакт, місто…"
        className="h-10 w-full rounded-md border border-input bg-background pl-10 pr-10 text-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
      {value && (
        <button type="button" onClick={() => onSubmit("")}
          className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-accent">
          <X className="h-4 w-4" />
        </button>
      )}
    </form>
  );
}

function QuickSegments({
  seg, segment, onPick,
}: {
  seg: Record<string, number | undefined>;
  segment: string;
  onPick: (s: string) => void;
}) {
  // ①/② — operational saved-views ONLY (scope живе в «Видимість», не тут).
  const always: Array<{ k: string; label: string; count?: number; active: boolean; tone?: "attn" }> = [
    { k: "overdueDebt", label: "З прострочкою", count: seg.overdueDebt, active: segment === "overdueDebt" },
    { k: "noOrdersThisMonth", label: "Без замовлень міс.", count: seg.noOrdersThisMonth, active: segment === "noOrdersThisMonth" },
    { k: "attention", label: "Потребують уваги", count: seg.attention, active: segment === "attention", tone: "attn" },
    { k: "unclassified", label: "Не класифіковані", count: seg.unclassified, active: segment === "unclassified" },
  ];
  const more: Array<{ k: string; label: string; count?: number }> = [
    { k: "breeders", label: "Заводчики", count: seg.breeders },
    { k: "holdings", label: "Холдинги", count: seg.holdings },
    { k: "technical", label: "Технічні / службові", count: seg.technical },
    { k: "profileOnReview", label: "Профіль на перевірці", count: seg.profileOnReview },
    { k: "highRisk", label: "Високий ризик", count: seg.highRisk },
    { k: "noNextAction", label: "Без next-action", count: seg.noNextAction },
    { k: "noPrimaryContact", label: "Без осн. контакту", count: seg.noPrimaryContact },
    { k: "missingProfile", label: "Профіль не заповнений", count: seg.missingProfile },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2">
      {always.map((c) => (
        <Chip key={c.k} active={c.active} tone={c.tone} onClick={() => onPick(c.k)} count={c.count}
          title={c.k === "attention" ? "агрегат: прострочка · немає контакту · немає профілю · не класифіковано · немає next-action · ризик" : undefined}>
          {c.label}
        </Chip>
      ))}
      <MoreSegmentsDropdown more={more} segment={segment} onPick={onPick} />
    </div>
  );
}

/**
 * BUG-B fix — «Ще» is a CONTROLLED dropdown (was a native <details> that never
 * closed): toggle on the button, outside-click closes, picking an item applies
 * the segment AND closes (single-select — consistent, always dismissible).
 */
function MoreSegmentsDropdown({
  more, segment, onPick,
}: {
  more: Array<{ k: string; label: string; count?: number }>;
  segment: string;
  onPick: (s: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const moreActive = more.some((c) => c.k === segment);

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          "cursor-pointer rounded-full border px-3 py-1.5 text-xs font-medium hover:text-foreground",
          moreActive ? "border-primary bg-primary/10 font-semibold text-primary" : "text-muted-foreground",
        )}
      >
        Ще {open ? "▴" : "▾"}
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-56 rounded-lg border bg-card p-1 shadow-lg">
          {more.map((c) => (
            <button key={c.k} type="button" onClick={() => { onPick(c.k); setOpen(false); }}
              className={cn("flex w-full items-center justify-between rounded px-2.5 py-1.5 text-left text-xs hover:bg-accent",
                segment === c.k && "bg-primary/10 font-semibold text-primary")}>
              <span>{c.label}</span>
              {c.count != null && <span className="text-muted-foreground">{c.count}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Chip({
  children, active, count, tone, onClick, title,
}: {
  children: React.ReactNode; active: boolean; count?: number; tone?: "attn";
  onClick: () => void; title?: string;
}) {
  return (
    <button type="button" onClick={onClick} title={title}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
        active
          ? tone === "attn"
            ? "border-warning-foreground/30 bg-warning text-warning-foreground"
            : "border-primary bg-primary/10 text-primary"
          : tone === "attn"
            ? "border-warning-foreground/20 bg-warning/50 text-warning-foreground hover:bg-warning"
            : "border-border text-foreground hover:border-primary/50",
      )}>
      {children}
      {count != null && <span className="rounded-full bg-black/5 px-1.5 text-[11px] dark:bg-white/10">{count}</span>}
    </button>
  );
}

function FilterSelect({
  value, onChange, placeholder, options, disabled, facet, full,
}: {
  value: string; onChange: (v: string) => void; placeholder: string;
  options: Array<{ v: string; label: string; n?: number }>;
  disabled?: boolean; facet?: Record<string, number>; full?: boolean;
}) {
  return (
    <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
      className={cn("h-9 rounded-md border border-input bg-background px-2.5 text-xs disabled:opacity-40",
        full && "w-full",
        value && "border-primary/50 font-medium text-primary")}>
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const n = o.n ?? facet?.[o.v];
        return <option key={o.v} value={o.v}>{o.label}{n != null ? ` (${n})` : ""}</option>;
      })}
    </select>
  );
}

function RegistryTable({
  rows, cols, has, biDown, expanded, onToggle, onOpen, onOpenMember,
}: {
  rows: ClientRow[]; cols: Col[]; has: (c: Col) => boolean; biDown: boolean;
  expanded: string[]; onToggle: (id: string) => void;
  onOpen: (id: string) => void; onOpenMember: (hid: string, aid: string) => void;
}) {
  const expSet = new Set(expanded);
  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
            <Th>Клієнт</Th>
            <Th>Менеджер</Th>
            {has("abc") && <Th center>ABC</Th>}
            {has("risk") && <Th center>Ризик</Th>}
            {has("rev365") && <Th right>Виручка 365д</Th>}
            {has("rev30") && <Th right>Виручка 30д</Th>}
            {has("debt") && <Th right>Борг</Th>}
            {has("overdue") && <Th right>Прострочено</Th>}
            {has("lastOrder") && <Th>Останнє замовл.</Th>}
            {has("lastContact") && <Th>Ост. контакт</Th>}
            {has("nextAction") && <Th>Наступна дія</Th>}
            <Th></Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <RegistryRow key={r.id} r={r} cols={cols} has={has} biDown={biDown}
              open={expSet.has(r.id)} onToggle={() => onToggle(r.id)}
              onOpen={() => onOpen(r.id)} onOpenMember={(aid) => onOpenMember(r.id, aid)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RegistryRow({
  r, has, biDown, open, onToggle, onOpen, onOpenMember,
}: {
  r: ClientRow; cols: Col[]; has: (c: Col) => boolean; biDown: boolean;
  open: boolean; onToggle: () => void; onOpen: () => void; onOpenMember: (aid: string) => void;
}) {
  const isHolding = r.type === "holding";
  const subtitle = [
    r.isService ? "службовий" : isHolding ? "холдинг" : "одиночний",
    r.businessModel ? (BUSINESS_MODEL_LABEL[r.businessModel] ?? r.businessModel) : (r.recordType !== "unknown" ? (RECORD_TYPE_PILL[r.recordType] ?? "") : ""),
    r.region,
  ].filter(Boolean).join(" · ");
  const debt = r.overdue && r.overdue > 0 ? r.overdue : (r.ar ?? 0);
  const colCount = 3 + ["abc", "risk", "rev365", "rev30", "debt", "overdue", "lastOrder", "lastContact", "nextAction"].filter((c) => has(c as Col)).length;

  return (
    <>
      <tr className={cn("border-b align-middle hover:bg-muted/30", r.isService && "text-muted-foreground italic")}>
        <td className="px-3 py-2.5">
          <div className="flex items-start gap-2">
            {isHolding ? (
              <button onClick={onToggle} className="mt-0.5 text-muted-foreground hover:text-primary" title="Юрособи">
                {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : <span className="w-4" />}
            <button onClick={onOpen} className="min-w-0 text-left">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={cn("font-semibold not-italic", r.archived && "text-muted-foreground")}>{r.name}</span>
                {r.archived && (
                  <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium not-italic text-muted-foreground">
                    <Archive className="h-3 w-3" /> Архівний клієнт
                  </span>
                )}
                {isHolding && <span className="text-xs text-muted-foreground">· {r.memberCount} юрособи</span>}
                {!r.isService && r.attentionCount > 0 && (
                  <span title={r.dqFlags.map((f) => DQ_FLAG_LABEL[f] ?? f).join(" · ")}
                    className="rounded bg-warning px-1.5 py-0.5 text-[10.5px] font-semibold text-warning-foreground">
                    Потребують уваги: {r.attentionCount}
                  </span>
                )}
                {r.isService && (
                  <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium not-italic text-muted-foreground">службовий · без CRM-вимог</span>
                )}
              </div>
              <div className="mt-0.5 text-[11.5px] text-muted-foreground">{subtitle}</div>
            </button>
          </div>
        </td>
        <td className="px-3 py-2.5">{r.manager || "—"}</td>
        {has("abc") && <td className="px-3 py-2.5 text-center">{r.abc ? <span className={cn("rounded px-1.5 py-0.5 text-[11px] font-bold", ABC_BADGE[r.abc])}>{r.abc}</span> : "—"}</td>}
        {has("risk") && <td className="px-3 py-2.5 text-center"><span className={cn("inline-block h-2.5 w-2.5 rounded-full", biDown || !r.risk ? "bg-muted-foreground" : RISK_DOT[r.risk])} title={r.risk ? RISK_LABEL[r.risk] : ""} /></td>}
        {has("rev365") && <td className="px-3 py-2.5 text-right tabular-nums">{biDown ? "—" : uah(r.revenue)}</td>}
        {has("rev30") && <td className="px-3 py-2.5 text-right tabular-nums">{biDown ? "—" : uah(r.revenue30)}</td>}
        {has("debt") && <td className={cn("px-3 py-2.5 text-right tabular-nums", debt > 0 && "font-semibold text-warning-foreground")}>{biDown ? "—" : (debt > 0 ? uah(debt) : "—")}</td>}
        {has("overdue") && <td className={cn("px-3 py-2.5 text-right tabular-nums", (r.overdue ?? 0) > 0 && "font-semibold text-destructive-soft-foreground")}>{biDown ? "—" : ((r.overdue ?? 0) > 0 ? uah(r.overdue) : "—")}</td>}
        {has("lastOrder") && <td className="px-3 py-2.5 text-xs">{r.lastOrderAt ? dmy(r.lastOrderAt) : "—"}</td>}
        {/* ⑦ — порожній «Ост. контакт» = muted (не конкурує з next-action) */}
        {has("lastContact") && (
          <td className={cn("px-3 py-2.5 text-xs", !r.lastContactAt && "text-muted-foreground/40")}>
            {r.lastContactAt ? dmy(r.lastContactAt) : "—"}
          </td>
        )}
        {has("nextAction") && <td className="px-3 py-2.5 text-xs"><NextActionCell r={r} /></td>}
        {/* ⑦ — «Відкрити» = muted icon-action (row-click уже відкриває картку) */}
        <td className="px-3 py-2.5 text-right">
          <button onClick={onOpen} title="Відкрити картку" aria-label={`Відкрити: ${r.name}`}
            className="not-italic text-muted-foreground/60 hover:text-primary">
            <ArrowRight className="h-4 w-4" />
          </button>
        </td>
      </tr>
      {isHolding && open && r.members.map((m) => (
        <tr key={m.id} className="border-b bg-muted/20 text-[12.5px]">
          <td className="py-1.5 pl-12 pr-3">
            <button onClick={() => onOpenMember(m.id)} className="text-left text-muted-foreground hover:text-primary">
              ↳ {m.name}
              {!m.active && <span className="ml-1.5 rounded bg-muted px-1 text-[10px]">неактивна</span>}
              <span className="ml-1.5 text-[11px]">{m.code ? `код 1С ${m.code}` : ""}{m.edrpou !== "—" ? ` · ЄДРПОУ ${m.edrpou}` : ""}</span>
            </button>
          </td>
          <td className="px-3" colSpan={colCount - 2} />
          <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{m.ar > 0 ? num(m.ar) : ""}</td>
        </tr>
      ))}
    </>
  );
}

function NextActionCell({ r }: { r: ClientRow }) {
  // ⑦ — «Наступна дія» = яскравий operational signal.
  if (!r.nextAction) return <span className="font-semibold text-warning-foreground">Без next action ⚠</span>;
  const na = r.nextAction;
  if (na.overdue) return <span className="font-semibold text-destructive-soft-foreground" title={na.label}>прострочено ⚠ {na.date ? dmy(na.date) : ""}</span>;
  return (
    <span title={na.label} className="font-medium text-foreground">
      {na.label ? na.label.slice(0, 18) : "задача"}
      {na.date ? <span className="font-normal text-muted-foreground"> · {dmy(na.date)}</span> : null}
    </span>
  );
}

function Th({ children, right, center }: { children?: React.ReactNode; right?: boolean; center?: boolean }) {
  return <th className={cn("px-3 py-2.5 font-medium", right && "text-right", center && "text-center", !right && !center && "text-left")}>{children}</th>;
}

function ErrorBox({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="rounded-xl border bg-card px-4 py-12 text-center">
      <AlertTriangle className="mx-auto mb-2 h-6 w-6 text-destructive" />
      <div className="text-sm font-medium">Не вдалося завантажити список</div>
      <button onClick={onRetry} className="mt-3 rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground">Повторити</button>
    </div>
  );
}

function EmptyBox({ hasFilters, onReset }: { hasFilters: boolean; onReset: () => void }) {
  return (
    <div className="rounded-xl border border-dashed bg-card px-4 py-12 text-center">
      <Search className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
      <div className="text-sm font-medium">Нічого не знайдено</div>
      <div className="mt-1 text-sm text-muted-foreground">Спробуйте змінити пошук або фільтри.</div>
      {hasFilters && <button onClick={onReset} className="mt-3 text-sm font-medium text-primary">Скинути фільтри</button>}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-3 rounded-lg border bg-card px-4 py-3">
          <div className="h-4 w-4 animate-pulse rounded bg-muted" />
          <div className="flex-1 space-y-2"><div className="h-3.5 w-1/3 animate-pulse rounded bg-muted" /><div className="h-2.5 w-1/5 animate-pulse rounded bg-muted" /></div>
          <div className="h-3.5 w-20 animate-pulse rounded bg-muted" />
        </div>
      ))}
    </div>
  );
}
